import React, { useState, useEffect } from 'react'
import { X, User, Calendar, DollarSign, ChevronDown } from 'lucide-react'
import employeeService from '../../services/employeeService'
import salaryService from '../../services/salaryService'
import toast from 'react-hot-toast'

const GeneratePayslipModal = ({ isOpen, onClose }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [selectedMonth, setSelectedMonth] = useState('')
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [payslipData, setPayslipData] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null)



  // Month options
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ]

  // Year options (current year and previous/next years)
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

    // Load employees on component mount
  useEffect(() => {
    if (isOpen) {
      loadEmployees()
    }
  }, [isOpen])



  const loadEmployees = async () => {
    try {
      setLoading(true)
      const employeesResponse = await employeeService.getAllEmployees()
      const employeesList = employeesResponse.data.employees || []
      setEmployees(employeesList)
      
      if (employeesList.length === 0) {
        toast.error('No employees found')
      }
      
    } catch (error) {
      console.error('Error loading employees:', error)
      setEmployees([])
      toast.error('Error loading employees')
    } finally {
      setLoading(false)
    }
  }

  const handleGenerate = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee')
      return
    }

    try {
      setGenerating(true)
      const month = `${selectedYear}-${selectedMonth}`
      
      // First fetch detailed employee information to get designation
      const employeeResponse = await employeeService.getEmployeeById(selectedEmployee)
      let employeeDetails = null
      
      if (employeeResponse?.data?.employee) {
        employeeDetails = employeeResponse.data.employee
      } else if (employeeResponse?.employee) {
        employeeDetails = employeeResponse.employee
      }
      
      // Store employee details for display
      setSelectedEmployeeDetails(employeeDetails)
      
      // Call the generate endpoint to get payslip preview
      const response = await salaryService.generatePayslipPreview(selectedEmployee, month)

      
      if (response?.success && response?.data?.salary) {
        console.log('✅ Response validation passed, setting payslip data')
        const salaryData = response.data.salary;
        console.log('🔍 Extracted salary data:', salaryData)
        
        // Set the data
        setPayslipData({...salaryData}); // Force new object reference
        console.log('🔍 Called setPayslipData with:', salaryData)
        
        toast.success('Payslip generated successfully!')
      } else if (response?.salary) {
        // Try alternate structure
        console.log('� Trying alternate structure - direct salary')
        setPayslipData(response.salary);
      } else {
        toast.error(response?.message || 'Failed to generate payslip')
      }
    } catch (error) {
      console.error('Error generating payslip:', error)
      // Try different error message paths to catch backend validation messages
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Error generating payslip'
      toast.error(errorMessage)
    } finally {
      setGenerating(false)
    }
  }

  const handleCreatePayslip = async () => {
    if (!selectedEmployee || !payslipData) {
      toast.error('Please generate payslip first')
      return
    }

    try {
      setLoading(true)
      const month = `${selectedYear}-${selectedMonth}`
      
      // Send only employee ID and month for creation
      const createData = {
        employee: selectedEmployee,
        month: month
      }
      
      // Call the create salary endpoint to save the payslip
      const response = await salaryService.createSalary(createData)
      console.log('Created salary:', response)
      
      if (response?.salary) {
        // Create API returns {salary: {...}} structure
        toast.success('Payslip created successfully!')
        handleClose()
      } else {
        toast.error(response?.message || 'Failed to create payslip')
      }
    } catch (error) {
      console.error('Error creating payslip:', error)
      // Try different error message paths to catch backend validation messages
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Error creating payslip'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedEmployee('')
    setPayslipData(null)
    setSelectedEmployeeDetails(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Generate & Create Payslip
                </h3>
                <div className="text-xs text-gray-500">
                  Render time: {new Date().toLocaleTimeString()} | Employees: {employees.length}
                </div>
              </div>
              <button
                onClick={handleClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Selection Form */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Employee Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee *
                </label>
                
                <div className="relative">
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none"
                    disabled={loading}
                  >
                    <option value="">
                      {loading ? 'Loading employees...' : 'Select Employee'}
                    </option>
                    {employees.map((employee) => (
                      <option 
                        key={`employee-${employee._id}`}
                        value={employee._id}
                      >
                        {employee.fullName || employee.name || `Employee ${employee._id}`}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Year Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year *
                </label>
                <div className="relative">
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none"
                  >
                    {years.map((year) => (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Month Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month *
                </label>
                <div className="relative">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none"
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <div className="mb-6">
              <button
                onClick={handleGenerate}
                disabled={generating || !selectedEmployee}
                className="bg-gray-900 text-white px-6 py-2 rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>

            {/* Payslip Details (Read-only) */}
            {payslipData && (
              <div className="border-t pt-6">
                <h4 className="text-lg font-semibold mb-4">Payslip Preview</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee name</label>
                    <input
                      type="text"
                      value={selectedEmployeeDetails?.fullName || selectedEmployeeDetails?.name || 'Unknown Employee'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                    <input
                      type="text"
                      value={selectedEmployeeDetails?.job?.name || 'N/A'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary</label>
                    <input
                      type="text"
                      value={payslipData.basicSalary ? `Rs. ${payslipData.basicSalary.toFixed(2)}` : 'Rs. 0.00'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month/Year</label>
                    <input
                      type="text"
                      value={(() => {
                        const date = new Date(payslipData.month);
                        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      })()}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gross Salary</label>
                    <input
                      type="text"
                      value={`Rs. ${(payslipData.grossSalary || 0).toFixed(2)}`}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Overtime (hours)</label>
                    <input
                      type="text"
                      value={`${payslipData.overtimeDurationInHours || 0} hrs`}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Extra Leaves (days)</label>
                    <input
                      type="text"
                      value={`${payslipData.extraLeaves || 0} days`}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                </div>

                {/* Allowances and Deductions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                  {/* Allowances */}
                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Allowances</h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600">Bonus Amount</label>
                        <input type="text" value={`Rs. ${(payslipData.allowances?.bonus || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Overtime Amount</label>
                        <input type="text" value={`Rs. ${(payslipData.allowances?.overtime || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">House</label>
                        <input type="text" value={`Rs. ${(payslipData.allowances?.house || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Transport</label>
                        <input type="text" value={`Rs. ${(payslipData.allowances?.transport || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Foods</label>
                        <input type="text" value={`Rs. ${(payslipData.allowances?.food || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Health</label>
                        <input type="text" value={`Rs. ${(payslipData.allowances?.health || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Deductions */}
                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Deductions</h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600">Extra Leave deductions</label>
                        <input type="text" value={`Rs. ${(payslipData.deductions?.extraLeaves || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Pre Payments</label>
                        <input type="text" value={`Rs. ${(payslipData.deductions?.prePayments || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">EPF - Employee Contribution(8%)</label>
                        <input type="text" value={`Rs. ${(payslipData.deductions?.epfEmployee || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Tax</label>
                        <input type="text" value={`Rs. ${(payslipData.deductions?.tax || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                      </div>
                      <div className="pt-2">
                        <h6 className="font-medium text-gray-800 text-sm mb-2">Company Contributions</h6>
                        <div>
                          <label className="block text-sm text-gray-600">EPF - Employer Contribution(12%)</label>
                          <input type="text" value={`Rs. ${(payslipData.epfEmployer || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                        </div>
                        <div className="mt-2">
                          <label className="block text-sm text-gray-600">ETF - Employer Contribution(3%)</label>
                          <input type="text" value={`Rs. ${(payslipData.etfEmployer || 0).toFixed(2)}`} readOnly className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-700">Total Allowances</div>
                    <div className="text-xl font-bold text-green-600">Rs. {(() => {
                      const allowances = payslipData.allowances || {};
                      const total = (allowances.bonus || 0) + (allowances.house || 0) + (allowances.transport || 0) + (allowances.food || 0) + (allowances.health || 0) + (allowances.overtime || 0);
                      return total.toFixed(2);
                    })()}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Total Deduction</div>
                    <div className="text-xl font-bold text-red-600">Rs. {(() => {
                      const deductions = payslipData.deductions || {};
                      const total = (deductions.prePayments || 0) + (deductions.extraLeaves || 0) + (deductions.epfEmployee || 0) + (deductions.tax || 0);
                      return total.toFixed(2);
                    })()}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Gross Salary</div>
                    <div className="text-xl font-bold text-blue-600">Rs. {(payslipData.grossSalary || 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Net Payable Salary</div>
                    <div className="text-2xl font-bold text-green-700 bg-green-50 px-2 py-1 rounded">Rs. {(payslipData.netSalary || 0).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {payslipData && (
              <button
                type="button"
                onClick={handleCreatePayslip}
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Payslip'}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GeneratePayslipModal